/**
 * Slip — the counter-slip surface language for every settings/account screen.
 *
 * WHY THIS EXISTS
 *
 * The old settings and account screens were a vertical stack of floating white
 * rounded cards, each holding two or three rows. That arrangement has three
 * concrete failures, not just an aesthetic one:
 *
 *   1. It hides the group hierarchy. Eight equal-weight cards separated by
 *      equal-weight gaps read as eight peers; nothing says "these two rows are
 *      the same subject and those three are a different one".
 *   2. It hides state. A row that only shows its NAME forces the user to open
 *      it to find out what it is currently set to — which for a first-time
 *      renovator on a phone in a bazaar is a wasted trip every single time.
 *   3. It is the #1 generated-UI tell (identical cards used as page structure).
 *
 * The replacement is the direction's counter slip (товарный чек): ONE ruled
 * sheet per screen, divided into bands by a DRAWN rule (`--g-rule-strong`),
 * with a sunken caption stub on the left (desktop) or above (mobile) naming the
 * band, and rows inside each band separated by the lighter drawn rule
 * (`.g-ruled` → `--g-rule`). The armature is visible, not implied by
 * whitespace, and the two rule weights carry the whole hierarchy so no card,
 * shadow, or coloured stripe has to.
 *
 * Every row is built from the same triplet — NAME (+ why) / VALUE / AFFORDANCE
 * — which is the "label grid" raise: a wall of rows reads as one instrument
 * because each one is ruled the same way. `valueAsData` puts the value in
 * `.g-data` (IBM Plex Mono, tabular, dotted zero) when it is genuinely a
 * figure or a code — a phone number, a timestamp, a masked IP, an ID — and
 * never when it is prose.
 *
 * `useSeenMark` implements the change-persistence raise: a row that just
 * changed keeps the `.g-mark` wash IN PLACE (it never re-sorts, never fades on
 * a timer) until the user's next interaction, which is what "seen" means here.
 *
 * Tokens only: `--g-rule`, `--g-rule-strong`, `--g-sunken`, `--g-mark`,
 * `--g-surface`, `--g-ink*`, `--g-error` via the shadcn `destructive` chain.
 * No hex, no shadow-as-structure, no nested cards.
 */
import clsx from "clsx";
import { ChevronRight } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { iconProps } from "@/lib/icons";

/** Focus ring shared by every interactive slip element (inset — rows are flush). */
export const SLIP_FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset";

/* ────────────────────────────── page + header ───────────────────────────── */

export interface SlipPageProps {
  children: ReactNode;
  testId?: string;
}

/**
 * The screen ground + reading column. Capped at 760px because a settings row is
 * a two-column statement (name | value), not a paragraph — wider and the value
 * drifts a hand's width away from its label on desktop.
 */
export function SlipPage({ children, testId }: SlipPageProps): ReactElement {
  return (
    <div className="min-h-full bg-[var(--g-ground)]" data-testid={testId}>
      <div className="mx-auto w-full max-w-[760px] px-4 pb-28 pt-6 md:px-6 md:pb-16 md:pt-10">
        {children}
      </div>
    </div>
  );
}

export interface SlipTitleProps {
  title: string;
  lede?: string;
  /** Right-hand meta (e.g. the signed-in account), shown from `sm` up. */
  meta?: ReactNode;
}

/**
 * The slip's masthead. `font-display` (Onest at 800) is the screen-title voice —
 * used here and nowhere else on the screen, so it stays signage rather than
 * decoration.
 */
export function SlipTitle({ title, lede, meta }: SlipTitleProps): ReactElement {
  return (
    <header className="mb-5 flex items-end justify-between gap-4 md:mb-7">
      <div className="min-w-0">
        <h1 className="font-display text-[27px] font-extrabold leading-[1.05] text-foreground md:text-[34px]">
          {title}
        </h1>
        {lede != null && lede !== "" ? (
          <p className="mt-2 max-w-[46ch] text-[14px] leading-[1.5] text-[var(--g-ink-2)]">
            {lede}
          </p>
        ) : null}
      </div>
      {meta != null ? (
        <div className="hidden shrink-0 text-right sm:block">{meta}</div>
      ) : null}
    </header>
  );
}

/* ────────────────────────────── the sheet ───────────────────────────────── */

export interface SlipProps {
  children: ReactNode;
  className?: string;
  testId?: string;
}

/** The ruled sheet itself: one border, no shadow — drawn, never floating. */
export function Slip({ children, className, testId }: SlipProps): ReactElement {
  return (
    <div
      data-testid={testId}
      className={clsx(
        "overflow-hidden rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface SlipSectionProps {
  /** The band's name. Rendered as the sunken caption stub. */
  caption: string;
  /** One line saying what the band is for / why it is asked. */
  note?: string;
  /** Heading level — h2 on a screen whose h1 is the page title, else h3. */
  headingAs?: "h2" | "h3";
  children: ReactNode;
  testId?: string;
}

/**
 * A band of the slip: sunken caption stub + ruled rows.
 *
 * Desktop puts the stub in a left column with a drawn vertical rule (a ledger's
 * stub); mobile stacks it as a full-width band above its rows. Both read the
 * same way — the group name is armature, not a floating label.
 */
export function SlipSection({
  caption,
  note,
  headingAs = "h2",
  children,
  testId,
}: SlipSectionProps): ReactElement {
  const Heading = headingAs;
  return (
    <section
      data-testid={testId}
      className="border-t border-[var(--g-rule-strong)] first:border-t-0 md:grid md:grid-cols-[13rem_1fr]"
    >
      <div className="border-b border-[var(--g-rule)] bg-[var(--g-sunken)] px-4 py-3 md:border-b-0 md:border-r md:px-5 md:py-5">
        <Heading className="text-[11px] font-bold uppercase leading-tight tracking-[0.11em] text-[var(--g-ink-2)]">
          {caption}
        </Heading>
        {note != null && note !== "" ? (
          <p className="mt-1.5 max-w-[34ch] text-[12px] leading-snug text-[var(--g-ink-2)]">
            {note}
          </p>
        ) : null}
      </div>
      {/*
        `min-w-0` is load-bearing, not tidiness: a grid item defaults to
        `min-width: auto`, so a row whose content is wider than the `1fr` track
        GROWS the track instead of letting its own `truncate` fire. Without it
        the desktop rows pushed their value column past the sheet's edge and the
        `overflow-hidden` clipped it — the value silently disappeared.
      */}
      <div className="g-ruled min-w-0">{children}</div>
    </section>
  );
}

/* ────────────────────────────── rows ────────────────────────────────────── */

export interface SlipRowProps {
  /** What the row is. */
  label: string;
  /** Why it exists / what happens — one short line under the label. */
  hint?: string;
  /**
   * The row's CURRENT STATE. A settings row that does not show its value makes
   * the user open it to find out, so this is close to mandatory.
   */
  value?: ReactNode;
  /** Put the value in the instrument face — figures, codes, timestamps only. */
  valueAsData?: boolean;
  /** Leading slot (an avatar, a status dot). Icons are optional, never required. */
  leading?: ReactNode;
  /** Trailing control (a switch, a button). Mutually exclusive with `to`/`onClick`. */
  trailing?: ReactNode;
  /** Navigate on activate — renders the row as a link with a chevron. */
  to?: string;
  /** Activate handler — renders the row as a button with a chevron. */
  onClick?: () => void;
  /** Suppress the chevron on an interactive row (e.g. it triggers, not navigates). */
  hideChevron?: boolean;
  /** Danger typing for a destructive row. */
  danger?: boolean;
  /** Change-persistence wash — set by `useSeenMark`. */
  marked?: boolean;
  /** A wide control rendered full-width beneath the label line. */
  children?: ReactNode;
  testId?: string;
  /** Test id on the label text itself (some specs assert the value in place). */
  labelTestId?: string;
  /** Test id on the value text itself. */
  valueTestId?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

function RowBody({
  label,
  hint,
  value,
  valueAsData,
  leading,
  trailing,
  danger,
  interactive,
  hideChevron,
  labelTestId,
  valueTestId,
  children,
}: SlipRowProps & { interactive: boolean }): ReactElement {
  return (
    <>
      <div className="flex w-full items-center gap-3.5">
        {leading != null ? <span className="shrink-0">{leading}</span> : null}
        <span className="min-w-0 flex-1">
          <span
            data-testid={labelTestId}
            className={clsx(
              "block truncate text-[14.5px] font-semibold leading-snug",
              danger === true ? "text-[var(--g-error)]" : "text-foreground",
            )}
          >
            {label}
          </span>
          {/* The hint WRAPS (to two lines) rather than truncating: it carries
              the "why", and half a reason is worse than none. The label above
              it stays single-line so the row height stays predictable. */}
          {hint != null && hint !== "" ? (
            // No `block` here — `line-clamp-2` sets `display:-webkit-box`, and
            // a later display utility silently cancels the clamp.
            <span className="mt-0.5 line-clamp-2 text-[12.5px] leading-snug text-[var(--g-ink-2)]">
              {hint}
            </span>
          ) : null}
        </span>
        {value != null ? (
          <span
            data-testid={valueTestId}
            className={clsx(
              "min-w-0 max-w-[46%] shrink truncate text-right text-[13.5px] text-[var(--g-ink-2)]",
              valueAsData === true && "g-data text-[12.5px]",
            )}
          >
            {value}
          </span>
        ) : null}
        {trailing != null ? <span className="shrink-0">{trailing}</span> : null}
        {interactive && hideChevron !== true ? (
          <ChevronRight
            {...iconProps("md")}
            className="shrink-0 text-[var(--g-ink-3)]"
          />
        ) : null}
      </div>
      {children != null ? <div className="mt-3.5 w-full">{children}</div> : null}
    </>
  );
}

/**
 * One ruled row. Renders as a `<Link>`, a `<button>`, or a plain `<div>`
 * depending on which activation prop it is given — the same anatomy either way,
 * so a screen of mixed rows still reads as one grid.
 */
export function SlipRow(props: SlipRowProps): ReactElement {
  const { to, onClick, marked, testId, ariaLabel, disabled } = props;
  const interactive = to != null || onClick != null;

  const base = clsx(
    "flex min-h-[56px] w-full flex-col items-start px-4 py-3.5 text-left md:px-5",
    marked === true && "g-mark",
    interactive &&
      "transition-colors duration-150 hover:bg-[var(--g-sunken)] active:bg-[var(--g-sunken)]",
    interactive && SLIP_FOCUS,
    disabled === true && "opacity-60",
  );

  if (to != null) {
    return (
      <Link to={to} data-testid={testId} aria-label={ariaLabel} className={base}>
        <RowBody {...props} interactive />
      </Link>
    );
  }
  if (onClick != null) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled === true}
        data-testid={testId}
        aria-label={ariaLabel}
        className={clsx(base, "disabled:cursor-not-allowed")}
      >
        <RowBody {...props} interactive />
      </button>
    );
  }
  return (
    <div data-testid={testId} className={base}>
      <RowBody {...props} interactive={false} />
    </div>
  );
}

export interface SlipMessageProps {
  tone: "error" | "muted";
  children: ReactNode;
  testId?: string;
  role?: "alert" | "status";
  /** A recovery affordance rendered beside the message. */
  action?: ReactNode;
}

/** An in-slip state line (a load failure with its retry, an empty list). */
export function SlipMessage({
  tone,
  children,
  testId,
  role,
  action,
}: SlipMessageProps): ReactElement {
  return (
    <div
      data-testid={testId}
      role={role}
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-5"
    >
      <p
        className={clsx(
          "text-[13.5px] leading-snug",
          tone === "error" ? "text-[var(--g-error)]" : "text-[var(--g-ink-2)]",
        )}
      >
        {children}
      </p>
      {action}
    </div>
  );
}

/* ────────────────────────────── danger zone ─────────────────────────────── */

export interface DangerZoneProps {
  title: string;
  /** What the user is being warned about, stated before they can act. */
  note?: string;
  children: ReactNode;
  testId?: string;
}

/**
 * Destructive actions, spatially AND visually separated from ordinary rows:
 * its own sheet, below a deliberate gap, in the semantic danger token. Nothing
 * here can be reached by a mis-tap while scanning the ordinary settings above.
 */
export function DangerZone({
  title,
  note,
  children,
  testId,
}: DangerZoneProps): ReactElement {
  return (
    <section className="mt-10 md:mt-14" data-testid={testId}>
      <h2 className="mb-2.5 px-1 text-[11px] font-bold uppercase tracking-[0.11em] text-[var(--g-error)]">
        {title}
      </h2>
      <div className="g-ruled overflow-hidden rounded-[var(--g-radius-lg)] border border-destructive/35 bg-destructive/[0.05]">
        {children}
      </div>
      {note != null && note !== "" ? (
        <p className="mt-2.5 px-1 text-[12.5px] leading-snug text-[var(--g-ink-2)]">
          {note}
        </p>
      ) : null}
    </section>
  );
}

/* ────────────────────────────── change persistence ──────────────────────── */

export interface SeenMark {
  /** The id of the row that changed and has not been seen yet. */
  marked: string | null;
  /** Mark a row as just-changed. */
  mark: (id: string) => void;
}

/**
 * The change-persistence raise. A row that changed holds `.g-mark` IN PLACE —
 * it never re-sorts and never fades on a timer. The mark clears on the user's
 * next deliberate interaction, which is what "seen" means; the short arming
 * delay keeps the very click that caused the change from clearing its own mark.
 */
export function useSeenMark(): SeenMark {
  const [marked, setMarked] = useState<string | null>(null);
  const armed = useRef(false);

  useEffect(() => {
    if (marked === null) return;
    armed.current = false;
    const armId = window.setTimeout(() => {
      armed.current = true;
    }, 350);
    const clear = (): void => {
      if (armed.current) setMarked(null);
    };
    document.addEventListener("pointerdown", clear, true);
    document.addEventListener("keydown", clear, true);
    return () => {
      window.clearTimeout(armId);
      document.removeEventListener("pointerdown", clear, true);
      document.removeEventListener("keydown", clear, true);
    };
  }, [marked]);

  return { marked, mark: setMarked };
}
